import { Container, Row, Col, Form, Card, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SpinnerButton from '../../../components/Button/SpinnerButton';
import { useState, useRef } from 'react';
import { RiSparklingFill, RiInformationLine, RiFileCopyFill } from '@remixicon/react';
import { authFetch } from '../../../utils/apiClient.js';
import CustomModal from '../../../components/Modal/CustomModal.jsx';
import LanguageSelect from '../../../components/LanguageSelect/LanguageSelect.jsx';
import Copied from '../../../components/Copied/Copied';
import { compressFile, handleCopyToClipboard } from '../../../utils/helpers.js';
import InfoBanner from '../../../components/InfoBanner/InfoBanner.jsx';
import useUsageStore from '../../../stores/usageStore.js';
import useProductStore from '../../../stores/productStore.js';
import AccessModal from '../../../components/Modal/AccessModal.jsx';
import useAccessType from '../../../hooks/useAccessType.js';
import DemoModal from '../../../components/Modal/DemoModal.jsx';

function ImageAltScreen() {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState({ load: false, generate: false });
    const [validated, setValidated] = useState(false);
    const [images, setImages] = useState([]);
    const [files, setFiles] = useState([]);
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('fr');
    const timerRef = useRef(null);
    const [showCopiedModal, setShowCopiedModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const { usage } = useUsageStore();
    const { currentProduct } = useProductStore();
    const [modalType, setModalType] = useState(null);
    const { getAccessType, isDemo } = useAccessType(true);

    const hasSufficientQuota = (count) => {
        const limit = parseInt(currentProduct.metadata?.limit, 10);
        const generations = usage.usage;
        const remainingQuota = limit - generations;
        return remainingQuota >= count;
    };

    const handleLoadImages = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            setValidated(true);
            return;
        }

        setValidated(false);
        try {
            const accessType = getAccessType();
            if (accessType) {
                setModalType(accessType);
                setShowModal(true);
                return;
            }
            const fileInput = form.elements.formFileMultiple;
            const fileArray = Array.from(fileInput.files);
            const totalFileSize = files.length + fileArray.length;
            if (totalFileSize > 10) {
                setModalType('image_upload');
                setShowModal(true);
                return;
            }
            if (!isDemo && !hasSufficientQuota(totalFileSize)) {
                setModalType('quota');
                setShowModal(true);
                return;
            }
            setIsLoading((prev) => ({ ...prev, load: true }));
            const compressedFiles = [];
            const imageUrls = [];
            for (const file of fileArray) {
                const compressedFile = await compressFile(file);
                compressedFiles.push(compressedFile);
                imageUrls.push(URL.createObjectURL(compressedFile));
            }

            setFiles((prev) => [...prev, ...compressedFiles]);
            setImages((prev) => [...prev, ...imageUrls.map(url => ({ url, altText: null }))]);
            form.reset();
        } catch (error) {
            console.error("Error loading files: ", error.message);
        } finally {
            setIsLoading((prev) => ({ ...prev, load: false }));
        }
    };

    async function generateAltImages() {
        try {
            const formData = new FormData();
            files.forEach((file) => {
                formData.append('images', file);
            });
            formData.append('languageCode', selectedLanguage);

            const res = await authFetch("/api/image/analyze-batch", {
                method: "POST",
                body: formData,
            }, false);

            return await res.json()
        } catch (error) {
            console.error("Error generating image alt text: ", error);
            return null;
        }
    }

    const handleDemo = async () => {
        const res = {
            images: images.map((_, index) => t('image.alt.alternativeText', { index: index + 1 }))
        }
        setImages((prev) => prev.map((img, index) => ({
            ...img,
            altText: res.images[index]
        })));
        setIsLoading((prev) => ({ ...prev, generate: false }));
    }

    const handleLanguageConfirm = async () => {
        setShowLanguageModal(false);

        if (isDemo) {
            setShowModal(true);
            return;
        }

        try {
            const accessType = getAccessType();
            if (accessType) {
                setModalType(accessType);
                setShowModal(true);
                return;
            }
            const fileSize = files.length;
            if (fileSize > 10) {
                setModalType('image_upload');
                setShowModal(true);
                return;
            }
            if (!hasSufficientQuota(fileSize)) {
                setModalType('quota');
                setShowModal(true);
                return;
            }
            setIsLoading((prev) => ({ ...prev, generate: true }));
            const response = await generateAltImages();
            setImages((prev) => prev.map((img, index) => ({
                url: img.url,
                altText: response.images.map((item) => item.alt)[index]
            })));
        } catch (error) {
            console.error("Error generating alt texts: ", error);
        } finally {
            setIsLoading((prev) => ({ ...prev, generate: false }));
        }
    };

    return (
        <Container className="py-5">
            <Row className="mb-5">
                <Col xs={12} className="text-center">
                    <h2 className="title">{t('image.alt.title')}</h2>
                </Col>
            </Row>

            {isDemo && (
                <InfoBanner />
            )}

            <Card>
                <Card.Body>
                    <Row className="mb-4">
                        <Col xs={12}>
                            <Form noValidate validated={validated} onSubmit={handleLoadImages} id="imagesAltForm">
                                <Form.Group controlId="formFileMultiple">
                                    <Form.Label>{t('image.alt.inputLabel')}</Form.Label>
                                    <Form.Control
                                        disabled={isLoading.load || isLoading.generate}
                                        type="file"
                                        multiple
                                        required
                                        accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
                                    />
                                </Form.Group>
                            </Form>
                        </Col>
                    </Row>

                    {files.length > 1 && (
                        <Row>
                            <Col xs={12}>
                                <Alert variant={'primary'} className="d-flex align-items-center gap-2">
                                    <RiInformationLine size={20} className="flex-shrink-0" />
                                    {t('image.alt.info')}
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    <Row className="mb-4 g-4">
                        <Col xs={12} md={6}>
                            <SpinnerButton
                                type="submit"
                                form="imagesAltForm"
                                className="w-100 bg-pink"
                                disabled={isLoading.load || isLoading.generate}
                                label={t('image.alt.uploadImages')}
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <SpinnerButton
                                onClick={() => setShowLanguageModal(true)}
                                disabled={images.length === 0 || isLoading.load || isLoading.generate}
                                isLoading={isLoading.generate}
                                className="w-100 bg-pink"
                                label={<>
                                    <span className="me-2">{t('app.generate')}</span>
                                    <RiSparklingFill size={20} />
                                </>}
                            />
                        </Col>
                    </Row>

                    <Row className="g-4">
                        {images.map(({ url, altText }, index) => (
                            <Col key={index} xs={12} md={6} lg={3}>
                                <Card>
                                    <Card.Img variant="top" src={url} />
                                    <Card.Body>
                                        <div className="d-flex justify-content-end mb-3">
                                            <RiFileCopyFill
                                                onClick={() => handleCopyToClipboard(altText, timerRef, setShowCopiedModal)}
                                                size={20}
                                                role="button"
                                                className={`copy-icon${!altText ? ' disabled' : ''}`}
                                                title={t('article.copyHtml')}
                                            />
                                        </div>
                                        <Card.Text>
                                            {altText || t('image.alt.clickOnGenerate')}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}

                        {isLoading.load && (
                            <Col xs={12} md={6} lg={3}>
                                <Card>
                                    <div className="card-img-top d-flex align-items-center justify-content-center">
                                        <Spinner animation="border" role="status" variant="light">
                                            <span className="visually-hidden">{t('app.loading')}</span>
                                        </Spinner>
                                    </div>
                                    <Card.Body>
                                        <Card.Text>
                                            {t('app.loading')}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>

                </Card.Body>
            </Card>

            <CustomModal
                show={showLanguageModal}
                onClose={() => setShowLanguageModal(false)}
                title={t('image.alt.chooseLanguage')}
                message={<LanguageSelect value={selectedLanguage} onChange={setSelectedLanguage} />}
                actionLabel={t('app.generate')}
                onAction={handleLanguageConfirm}
                closeLabel={t('app.close')}
                actionClassName="bg-pink"
            />

            <AccessModal
                show={showModal && !isDemo}
                setShow={setShowModal}
                modalType={modalType}
            />

            <DemoModal
                show={showModal && isDemo}
                setShow={setShowModal}
                onAction={handleDemo}
            />

            <Copied
                show={showCopiedModal}
                onHide={() => setShowCopiedModal(false)}
            />

        </Container>
    );
}

export default ImageAltScreen;
